import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse } from '@/components/Collapse';
import { Box, Button, Text } from '@/ui';
import type { OrgMember } from '../hooks/useOrganizationMembers';
import { getSeatUsage } from '../lib/seatLimit';
import { AddMembersSection } from './AddMembersSection';
import { FamilyAddMembersSection } from './FamilyAddMembersSection';

type Props = {
  organizationId: string;
  members: OrgMember[];
  /** Lowercased emails that already have a pending invite, expired included. */
  pendingInviteEmails: string[];
  /**
   * Seats occupied: accepted members + live (non-expired) pending invites — the
   * same count the server's invite gate uses.
   */
  occupiedSeats: number;
  seatLimit: number | null | undefined;
  buttonLabel: string;
  /** Solid pink for a family's primary action, outline alongside a seat table. */
  isPrimary?: boolean;
  /**
   * The funding subscription has lapsed or stopped paying. Withholds the invite
   * form the same way a spent seat limit does — a defunded workspace grants its
   * members nothing, so adding more of them helps nobody. Removing members and
   * the roster itself stay available; only growing it is held back.
   */
  isPlanInactive?: boolean;
  /** Which reactivation sentence to show, family or business wording. */
  isFamily?: boolean;
  /**
   * Collect each invitee's name and phone number rather than just an email, so
   * they land text-ready. Family orgs only, everyone else has nowhere to put a
   * phone number.
   */
  collectsContactDetails?: boolean;
};

// Keeps the email field out of the way until the owner actually wants to invite
// someone — the roster is what they're usually here to look at — and swallows
// the button entirely once there are no seats left to fill.
export const InviteSection = ({
  organizationId,
  members,
  pendingInviteEmails,
  occupiedSeats,
  seatLimit,
  buttonLabel,
  isPrimary = false,
  isPlanInactive = false,
  isFamily = false,
  collectsContactDetails = false,
}: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'organizations.invite',
  });
  const [isOpen, setIsOpen] = useState(false);

  const { hasReachedLimit } = getSeatUsage(seatLimit, occupiedSeats);

  // Checked before the seat limit: a defunded workspace keeps its seatLimit
  // (kept for revival), so it can have seats free and still be the wrong place
  // to send an invitation. No upgrade CTA here — the plan card above owns that
  // action, matching how the spent-seats case defers too.
  if (isPlanInactive) {
    return (
      <Text color="customgray.mid" fontSize="sm">
        {t(isFamily ? 'plan_inactive_family' : 'plan_inactive_business')}
      </Text>
    );
  }

  if (hasReachedLimit) {
    return (
      <Text color="customgray.mid" fontSize="sm">
        {t('limit_reached')}
      </Text>
    );
  }

  return (
    <>
      <Collapse in={isOpen}>
        {collectsContactDetails ? (
          <FamilyAddMembersSection
            members={members}
            occupiedSeats={occupiedSeats}
            organizationId={organizationId}
            pendingInviteEmails={pendingInviteEmails}
            seatLimit={seatLimit}
            onClose={() => setIsOpen(false)}
          />
        ) : (
          <AddMembersSection
            members={members}
            occupiedSeats={occupiedSeats}
            organizationId={organizationId}
            pendingInviteEmails={pendingInviteEmails}
            seatLimit={seatLimit}
            onClose={() => setIsOpen(false)}
          />
        )}
      </Collapse>
      {!isOpen && (
        <Box>
          <Button
            variant={isPrimary ? 'solid' : 'secondary'}
            onClick={() => setIsOpen(true)}
          >
            {buttonLabel}
          </Button>
        </Box>
      )}
    </>
  );
};
